/**
 * Cards shared between the shop, the home page and search results.
 *
 * One component for a product wherever it appears. Two implementations — one for the
 * home page grid, one for the shop — is how the two drift, and the drift is always in
 * something like whether "out of stock" disables the button.
 */

import { Link } from 'react-router-dom';
import { Money, StockPill } from './ui.jsx';
import { ProductArtwork } from './Artwork.jsx';
import { useCart, toast } from '../lib/stores.js';
import type { ProductCard } from '../lib/queries.js';

export function ProductCardView({
  product,
  showAddToCart = true,
}: {
  product: ProductCard;
  showAddToCart?: boolean;
}) {
  const add = useCart((s) => s.add);
  const sellable = product.available > 0 || product.allowBackorder;

  return (
    <article
      className="card"
      style={{ background: 'var(--panel-2)', display: 'grid', gridTemplateRows: 'auto 1fr' }}
    >
      <Link to={`/shop/${product.slug}`} style={{ display: 'block', position: 'relative', aspectRatio: '4 / 3', background: 'var(--wash)' }}>
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.imageAlt ?? product.name}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          // Drawn artwork, not an empty box with a SKU in it. See components/Artwork.tsx.
          <ProductArtwork category={product.categorySlug} name={product.name} sku={product.sku} />
        )}
        <span style={{ position: 'absolute', top: 10, right: 10 }}>
          <StockPill state={product.stockState} available={product.available} />
        </span>
      </Link>

      {/*
        A FLEX column, not the grid `.stack`.
        The price-and-button row below sets `margin-top: auto` to sit at the foot of the
        card, which is what lines the four buttons up across a row of cards. In a grid
        container with `align-content: start` that does nothing — the rows pack at the
        top and the free space is left unallocated — so every card put its button
        directly under its own text and the row of them came out ragged, lower on the
        cards with two lines of description and lower still on the one with a struck-out
        price. In a flex column the auto margin does what it reads like.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 18 }}>
        {product.brandName ? (
          <p className="xs" style={{ color: 'var(--accent-deep)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {product.brandName}
          </p>
        ) : null}

        <Link to={`/shop/${product.slug}`} style={{ color: 'var(--ink)' }}>
          <h3 style={{ fontSize: 16, lineHeight: 1.3 }}>{product.name}</h3>
        </Link>

        {product.shortDescription ? (
          <p
            className="xs dim"
            /* Two lines, clamped. A card whose height depends on how much someone typed
               makes a grid of them look broken. */
            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {product.shortDescription}
          </p>
        ) : null}

        {/* Bottom-aligned and non-wrapping: the one product with a struck-out old price
            was wide enough to wrap its button onto a second line, which left that card's
            button sitting 6px higher than the other three across the row. */}
        <div className="spread" style={{ marginTop: 'auto', paddingTop: 8, gap: 10, alignItems: 'flex-end', flexWrap: 'nowrap' }}>
          <div style={{ minWidth: 0 }}>
            <p className="xs dim">Price ({product.currency})</p>
            <p style={{ fontWeight: 700, fontSize: 17, whiteSpace: 'nowrap' }}>
              <Money value={product.price} currency={product.currency} decimals={false} />
            </p>
            {/* The old price on its own line. Beside the new one it left about 40px for a
                five-figure rupee amount, so it broke in the middle of the number —
                "Rs. 245,000 Rs." above "268,000". */}
            {product.compareAtPrice ? (
              <p className="xs dim" style={{ textDecoration: 'line-through', whiteSpace: 'nowrap' }}>
                <Money value={product.compareAtPrice} currency={product.currency} decimals={false} />
              </p>
            ) : null}
          </div>

          {showAddToCart ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!sellable}
              onClick={() => {
                add({
                  productId: product.id,
                  quantity: 1,
                  name: product.name,
                  slug: product.slug,
                  unitPrice: product.price,
                  imageUrl: product.imageUrl,
                });
                toast.success(`${product.name} added to your cart.`);
              }}
            >
              {sellable ? 'Add to cart' : 'Unavailable'}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
